import { Component } from '@theme/component';

/**
 * @typedef {Object} NcnFeatureHighlightRefs
 * @property {HTMLElement} tabsNav - Navigation container for tabs
 * @property {HTMLElement} gallery - Gallery container for images
 * @property {HTMLElement[]} [tabs] - Tab button elements
 * @property {HTMLImageElement[]} [deferredImages] - Deferred gallery images
 */

/**
 * Vertical tab accordion with image transitions.
 *
 * @extends {Component<NcnFeatureHighlightRefs>}
 */
class NcnFeatureHighlightComponent extends Component {
  requiredRefs = ['tabsNav', 'gallery'];

  /** @type {number} */
  #activeIndex = 0;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#setActiveTab(0);
        this.setAttribute('ready', '');
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialized = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#setActiveTab(0);
        this.setAttribute('ready', '');
      });
    });
  }

  /**
   * Activates a tab by index. Called via declarative `on:click`.
   *
   * @param {number} index - Tab index to activate
   * @param {MouseEvent} _event - Click event (unused)
   */
  activateTab(index, _event) {
    this.#setActiveTab(index);
  }

  /**
   * Handles keyboard navigation between tabs. Called via declarative `on:keydown`.
   *
   * @param {number} currentIndex - Index of the focused tab
   * @param {KeyboardEvent} event - Keydown event
   */
  handleKeyNavigation(currentIndex, event) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    let newIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (currentIndex + 1) % tabs.length;
        tabs[newIndex]?.focus();
        this.#setActiveTab(newIndex);
        break;
      case 'ArrowUp':
        event.preventDefault();
        newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        tabs[newIndex]?.focus();
        this.#setActiveTab(newIndex);
        break;
      case 'Home':
        event.preventDefault();
        tabs[0]?.focus();
        this.#setActiveTab(0);
        break;
      case 'End': {
        event.preventDefault();
        const lastIndex = tabs.length - 1;
        tabs[lastIndex]?.focus();
        this.#setActiveTab(lastIndex);
        break;
      }
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#setActiveTab(currentIndex);
        break;
      case 'Escape':
        event.preventDefault();
        this.#collapseCurrentPanel();
        break;
    }
  }

  /**
   * Sets the active tab, updates ARIA states, and switches images.
   *
   * @param {number} index - Tab index to activate
   */
  #setActiveTab(index) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    if (this.#initialized && index === this.#activeIndex && tabs[index]?.classList.contains('active')) return;

    this.#activeIndex = index;

    for (const [i, btn] of tabs.entries()) {
      const isActive = i === index;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    const activeButton = tabs[index];
    if (activeButton) this.#displayImage(activeButton);

    const nextIndex = (index + 1) % tabs.length;
    this.#loadDeferredImage(nextIndex);

    this.#initialized = true;
  }

  /**
   * Updates the gallery to show the image for the given tab button.
   *
   * @param {HTMLElement} button - The active tab button
   */
  #displayImage(button) {
    const { gallery } = this.refs;
    if (!gallery) return;

    const index = button.getAttribute('data-index');
    gallery.setAttribute('data-active-index', index ?? '0');
    this.#loadDeferredImage(parseInt(index ?? '0', 10));
  }

  /**
   * Swaps data-srcset/data-sizes/data-src to real attributes on first activation.
   *
   * @param {number} index - The data-index of the image to load
   */
  #loadDeferredImage(index) {
    const { deferredImages } = this.refs;
    if (!deferredImages) return;

    const img = deferredImages.find(
      (el) => el.getAttribute('data-index') === String(index)
    );
    if (!img) return;

    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      delete img.dataset.srcset;
    }
    if (img.dataset.sizes) {
      img.sizes = img.dataset.sizes;
      delete img.dataset.sizes;
    }
    if (img.dataset.src) {
      img.src = img.dataset.src;
      delete img.dataset.src;
    }
    img.classList.remove('ncn-feature-highlight__image--deferred');
  }

  /** Collapses all panels and resets gallery to default. */
  #collapseCurrentPanel() {
    const { tabs, gallery } = this.refs;
    if (!tabs?.length) return;

    for (const btn of tabs) {
      btn.classList.remove('active');
      btn.setAttribute('aria-expanded', 'false');
    }

    gallery?.removeAttribute('data-active-index');
  }
}

if (!customElements.get('ncn-feature-highlight-component')) {
  customElements.define('ncn-feature-highlight-component', NcnFeatureHighlightComponent);
}
